import { addDays, parseISODate, toISODate } from "./format";

export const DEFAULT_PRESET = "12m";
export const CUSTOM_PRESET = "custom";

/** 1 July of the fiscal year (July to June) that `date` falls in. */
export const fiscalYearStart = (date) => new Date(date.getMonth() >= 6 ? date.getFullYear() : date.getFullYear() - 1, 6, 1);

export const PRESETS = [
  { key: "7d", label: "7 days", range: (today) => [addDays(today, -6), today] },
  { key: "30d", label: "30 days", range: (today) => [addDays(today, -29), today] },
  { key: "90d", label: "90 days", range: (today) => [addDays(today, -89), today] },
  { key: "mtd", label: "This month", range: (today) => [new Date(today.getFullYear(), today.getMonth(), 1), today] },
  { key: "ytd", label: "This year", range: (today) => [new Date(today.getFullYear(), 0, 1), today] },
  { key: "12m", label: "12 months", range: (today) => [new Date(today.getFullYear(), today.getMonth() - 11, 1), today] },
];

/* The books run on a July-June fiscal year, so the Accounts dashboard offers
   fiscal periods in place of the calendar year. "All time" has no start date:
   the API starts it at the first transaction on file. */
export const ALL_TIME_PRESET = "all";
export const ACCOUNTS_DEFAULT_PRESET = ALL_TIME_PRESET;

export const ACCOUNTS_PRESETS = [
  { key: ALL_TIME_PRESET, label: "All time", range: (today) => [null, today] },
  { key: "mtd", label: "This month", range: (today) => [new Date(today.getFullYear(), today.getMonth(), 1), today] },
  {
    key: "lm",
    label: "Last month",
    range: (today) => [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)],
  },
  { key: "90d", label: "90 days", range: (today) => [addDays(today, -89), today] },
  { key: "fy", label: "This FY", range: (today) => [fiscalYearStart(today), today] },
  {
    key: "lfy",
    label: "Last FY",
    range: (today) => {
      const start = fiscalYearStart(today);
      return [new Date(start.getFullYear() - 1, 6, 1), addDays(start, -1)];
    },
  },
  { key: "12m", label: "12 months", range: (today) => [new Date(today.getFullYear(), today.getMonth() - 11, 1), today] },
];

const findPreset = (key, presets) => presets.find((preset) => preset.key === key);

export const presetRange = (key, today = new Date(), presets = PRESETS, defaultPreset = DEFAULT_PRESET) => {
  const [from, to] = (findPreset(key, presets) || findPreset(defaultPreset, presets)).range(today);
  // A null start (All time) stays null for the API to resolve.
  return { from: from ? toISODate(from) : null, to: toISODate(to) };
};

/** Twelve calendar months ending on `lastDate` - used to jump to real activity. */
export const trailingYear = (lastDate) => {
  const end = parseISODate(lastDate);
  if (!end) return null;
  return { from: toISODate(new Date(end.getFullYear(), end.getMonth() - 11, 1)), to: lastDate };
};

/**
 * Reads the period from URL params, trusting nothing: unknown presets and
 * malformed or reversed dates fall back to sensible values instead of
 * reaching the API.
 */
export const periodFromParams = (params, today = new Date(), presets = PRESETS, defaultPreset = DEFAULT_PRESET) => {
  const from = parseISODate(params.get("from"));
  const to = parseISODate(params.get("to"));
  const requested = params.get("period");

  if (from && to) {
    const [start, end] = from <= to ? [from, to] : [to, from];
    const range = { from: toISODate(start), to: toISODate(end) };
    // A preset link opened on a later day recomputes to today's window.
    if (findPreset(requested, presets)) return { preset: requested, range: presetRange(requested, today, presets, defaultPreset) };
    return { preset: CUSTOM_PRESET, range };
  }

  const preset = findPreset(requested, presets) ? requested : defaultPreset;
  return { preset, range: presetRange(preset, today, presets, defaultPreset) };
};

/** Applies one edited bound, keeping from <= to. Returns null if the value is unusable. */
export const withBound = (range, field, value) => {
  if (!parseISODate(value)) return null;
  const next = { ...range, [field]: value };
  if (next.from > next.to) {
    if (field === "from") next.to = value;
    else next.from = value;
  }
  return next;
};
